import { Alert, Button, Group, Modal, Stack, Text } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { NapakaApi, sporocilo } from '../api/napaka';
import { api, podatki, type Shema } from '../api/odjemalec';
import { barve } from '../tema';

type Nalog = Shema['DelovniNalogPodrobnosti'];

/**
 * Podpis stranke ob sprejemu na zaslonu (prst ali pisalo na tablici ali telefonu, miška na računalniku). Podpis ni
 * obvezen: brez njega je na PDF-ju naloga prostor za ročni podpis. Strežnik obreže prazen rob in shrani PNG.
 */
export function PodpisStranke({
  nalog,
  odprto,
  zapri,
  posodobljen,
}: {
  nalog: Nalog;
  odprto: boolean;
  zapri: () => void;
  posodobljen: (n: Nalog) => void;
}) {
  return (
    <Modal opened={odprto} onClose={zapri} title="Podpis stranke" size="lg">
      {odprto && <Ploscica nalog={nalog} zapri={zapri} posodobljen={posodobljen} />}
    </Modal>
  );
}

function Ploscica({ nalog, zapri, posodobljen }: { nalog: Nalog; zapri: () => void; posodobljen: (n: Nalog) => void }) {
  const platno = useRef<HTMLCanvasElement>(null);
  const risem = useRef(false);
  const [prazno, setPrazno] = useState(true);

  // velikost risbe po širini okna (ostro tudi na zaslonih z več piksli na točko)
  useEffect(() => {
    const c = platno.current;
    if (!c) {
      return;
    }
    const r = window.devicePixelRatio || 1;
    c.width = c.clientWidth * r;
    c.height = c.clientHeight * r;
    const g = c.getContext('2d');
    if (g) {
      g.scale(r, r);
      g.lineWidth = 2.5;
      g.lineCap = 'round';
      g.lineJoin = 'round';
      g.strokeStyle = '#111';
      g.fillStyle = '#fff';
      g.fillRect(0, 0, c.width, c.height);
    }
  }, []);

  const tocka = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const b = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - b.left, y: e.clientY - b.top };
  };
  const pocisti = () => {
    const c = platno.current;
    const g = c?.getContext('2d');
    if (c && g) {
      g.save();
      g.setTransform(1, 0, 0, 1, 0, 0);
      g.fillRect(0, 0, c.width, c.height);
      g.restore();
    }
    setPrazno(true);
  };

  const shrani = useMutation({
    mutationFn: () => {
      const png = platno.current!.toDataURL('image/png').split(',')[1]!;
      return podatki(
        api.PUT('/api/delavnica/nalogi/{id}/podpis', {
          params: { path: { id: nalog.id! } },
          // byte[] v JSON je base64
          body: { slika: png },
        }),
      );
    },
    onSuccess: (n) => {
      posodobljen(n);
      zapri();
    },
  });

  return (
    <Stack gap="sm">
      <Text size="sm">
        {nalog.kupec ? `${nalog.kupec}, ` : ''}s podpisom potrjujete oddajo vozila in naročilo del na nalogu{' '}
        {nalog.stevilka}.
      </Text>
      <canvas
        ref={platno}
        aria-label="Polje za podpis"
        style={{
          width: '100%',
          height: 220,
          border: `1px solid ${barve.obroba}`,
          borderRadius: 10,
          touchAction: 'none',
          background: '#fff',
          cursor: 'crosshair',
        }}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          risem.current = true;
          const g = e.currentTarget.getContext('2d')!;
          const { x, y } = tocka(e);
          g.beginPath();
          g.moveTo(x, y);
          g.lineTo(x + 0.1, y + 0.1);
          g.stroke();
          setPrazno(false);
        }}
        onPointerMove={(e) => {
          if (!risem.current) {
            return;
          }
          const g = e.currentTarget.getContext('2d')!;
          const { x, y } = tocka(e);
          g.lineTo(x, y);
          g.stroke();
        }}
        onPointerUp={() => {
          risem.current = false;
        }}
        onPointerCancel={() => {
          risem.current = false;
        }}
      />
      {shrani.error && (
        <Alert color="red" variant="light" role="alert">
          {shrani.error instanceof NapakaApi && shrani.error.polja.podpis
            ? shrani.error.polja.podpis
            : sporocilo(shrani.error)}
        </Alert>
      )}
      <Group justify="space-between" gap="xs">
        <Button variant="subtle" onClick={pocisti} disabled={prazno || shrani.isPending}>
          Počisti
        </Button>
        <Group gap="xs">
          <Button variant="subtle" onClick={zapri} disabled={shrani.isPending}>
            Prekliči
          </Button>
          <Button onClick={() => shrani.mutate()} loading={shrani.isPending} disabled={prazno}>
            Shrani podpis
          </Button>
        </Group>
      </Group>
    </Stack>
  );
}
